import React, { Component, Fragment } from 'react';
import { arrayOf, shape, string, func, object, objectOf, bool, number } from 'prop-types';
import { get, orderBy, isEqual } from 'lodash';
import DataSelector from './DataSelector';
import SeriesSelector from './SeriesSelector';
import DaypartCompilationChart from '../DaypartCompilationChart';
import ModuleExport from '../../ModuleExport';
import { 
  DAYPART_COMPILATION_CHART_ID,
  GA4_DAYPART_MESSAGE
} from '../../../helpers/constant';
import DaypartTable from './DaypartTable';
import {
  getChartData,
  formatCutOverDateByTimeRange,
  withInRange,
  formatCutOverDate,
} from '../../../helpers/formatData';
import DeleteReportItemButton from '../../DeleteReportItem';
import { NewAdUserSunsetDisclaimer, UASunsetDisclaimer } from '../../DashboardTab/ChartDisclaimer';

import cx from 'classnames';
import styles from "./styles.scss";

const dataTypes = [{
  value: 'adVisit',
  label: 'Ad Visits'
},
{
  value: 'newAdUsers',
  label: 'New Ad Users'
},
];

const typeSeries = {
  [dataTypes[0].value]: {
    guidelineLabel: 'Ads',
    baseLabel: 'Non-Ad Visits',
    additionLabel: 'Ad Visits',
    totalLabel: 'Total Visits',
    yAxisLabel: dataTypes[0].label
  },
  [dataTypes[1].value]: {
    guidelineLabel: 'Ads',
    baseLabel: 'Non-New Ad Users',
    additionLabel: 'New Ad Users',
    totalLabel: 'Total Ad Users',
    yAxisLabel: dataTypes[1].label
  }
}

const initialSeriesVisibility = {
  hoverBaseSeries: false,
  hoverAdditionSeries: false,
  hoverGuidelineSeries: false
};

const initialSortByVisitPerAd = { sortKey: 'visitsPerAd', sortType: 'desc' };
const initialSortByNewAdUserPerAds = { sortKey: 'newAdUsersPerAds', sortType: 'desc' };
const initialSortByDate = { sortKey: 'sortTimeKey', sortType: 'desc' };

class DaypartDetailsViewer extends Component {
  static propTypes = {
    className: string,
    dateRange: string,
    baseColor: string,
    additionColor: string,
    guidelineColor: string,
    setChartSelector: func,
    chartData: arrayOf(object),
    daypartDetailSelector: shape({
      type: string,
      timeRange: string
    }),
    title: string,
    tableData: arrayOf(object),
    campaign: objectOf(object),
    onTableCellClick: func,
    inpdf: bool,
    page: number,
    numberPage: number,
    rowsPerPage: number,
    isGrandTotalDisplay: bool,
    isDaypartTab: bool,
    isCustomReport: bool,
    showInReportConfig: shape({
      daypartAnalyticsChart: bool,
      daypartAnalyticsTable: bool
    }),
    currentStep: string,
    itemIndex: number,
    tableSortSelector: shape({
      sortKey: string,
      sortType: string,
    }),
    isNewAdUsersEnabled: bool,
    ga4CutOverDate: string,
    showLocationDisclaimer: bool,
    advertiser: advertiserShape,
    isHiddenTable: bool,
    rowsFirstPage: number,
    displayChart: bool
  }

  static defaultProps = {
    baseColor: '#00BCD4',
    additionColor: '#3F51B5',
    guidelineColor: '#CF87DF',
    daypartDetailSelector: {
      type: 'adVisit',
      timeRange: 'byDayPart'
    },
    title: 'Daypart',
    chartData: [],
    showInReportConfig: {},
  }

  state = {
    ...initialSeriesVisibility,
    prevDailyData: [],
    prevHourlyData: [],
  }

  static getDerivedStateFromProps(props, state) {
    const { dailyData, hourlyData } = props;
    const { prevDailyData, prevHourlyData } = state;
    const sortedDailyData = orderBy(dailyData, ['time', 'ads', 'visit', 'adVisit', 'user', 'newUser']);
    const sortedHourlyData = orderBy(hourlyData, ['time', 'ads', 'visit', 'adVisit', 'user', 'newUser']);
    const sortedPrevDailyData = orderBy(prevDailyData, ['time', 'ads', 'visit', 'adVisit', 'user', 'newUser']);
    const sortedPrevHourlyData = orderBy(prevHourlyData, ['time', 'ads', 'visit', 'adVisit', 'user', 'newUser']);

    const timeRange = get(state, 'dataSelectOptions.hourly');
    if (
      (timeRange !== 'hourly' && !isEqual(sortedDailyData, sortedPrevDailyData)) ||
      (timeRange === 'hourly' && !isEqual(sortedHourlyData, sortedPrevHourlyData))
    ) {
      return {
        ...state,
        ...initialSeriesVisibility,
        prevDailyData: dailyData,
        prevHourlyData: hourlyData
      };
    }
    return state;

  }

  handleSerieSelectionHover = hoverInfo => {
    this.setState({
      ...hoverInfo
    });
  }

  removeYear(dateString) {
    const dateParts = dateString.split('/');
    return parseInt(dateParts[0]) + '/' + parseInt(dateParts[1]);
  }

  onSelectionChange = (updateSelector) => {
    const { setChartSelector, itemIndex } = this.props;
    setChartSelector(DAYPART_COMPILATION_CHART_ID, updateSelector, itemIndex);
  }

  prepCsvData(data) {
    const {
      type,
      showBaseSerie,
      showAdditionSerie,
      showGuidelineSerie
    } = this.props.daypartDetailSelector;

    const serieLabels = typeSeries[type];
    const csvTitle = ['Time'];
    showBaseSerie && csvTitle.push(serieLabels.baseLabel);
    showAdditionSerie && csvTitle.push(serieLabels.additionLabel);
    showGuidelineSerie && csvTitle.push(serieLabels.guidelineLabel);

    return data && data.reduce((accum, currentEntry) => {
      const { time, baseValue, additionalValue, guidelineValue } = currentEntry;
      const csvData = [time];
      showBaseSerie && csvData.push(baseValue);
      showAdditionSerie && csvData.push(additionalValue);
      showGuidelineSerie && csvData.push(guidelineValue);
      accum.push(csvData);
      return accum;
    }, [csvTitle]);
  }

  handleCellClick = (data) => {
    const { onTableCellClick } = this.props;
    onTableCellClick && onTableCellClick(data, DAYPART_COMPILATION_CHART_ID);
  }

  exportContainer = React.createRef();
  render() {
    const {
      dateRange,
      className,
      baseColor,
      additionColor,
      guidelineColor,
      chartData,
      daypartDetailSelector,
      title,
      tableData,
      campaign,
      inpdf,
      numberPage,
      rowsPerPage,
      isGrandTotalDisplay,
      isDaypartTab,
      page,
      isCustomReport,
      showInReportConfig,
      currentStep,
      itemIndex,
      tableSortSelector,
      isNewAdUsersEnabled,
      ga4CutOverDate,
      showLocationDisclaimer,
      advertiser,
      isHiddenTable,
      rowsFirstPage,
      displayChart
    } = this.props;
    const {
      // dataSelectOptions,
      hoverBaseSeries,
      hoverAdditionSeries,
      hoverGuidelineSeries,
    } = this.state;
    const {
      type = 'advisit',
      showBaseSerie,
      showAdditionSerie,
      showGuidelineSerie,
      timeRange
    } = daypartDetailSelector || {};
    const isShowNewAdUsers = isNewAdUsersEnabled && daypartDetailSelector.type === NEW_AD_USERS;

    const isByDateOption = ['daily', 'weekly', 'monthly'].includes(timeRange);
    const initialSortStatus = tableSortSelector ?
                                tableSortSelector :
                                isByDateOption ?
                                  initialSortByDate :
                                  isShowNewAdUsers ? initialSortByNewAdUserPerAds : initialSortByVisitPerAd;

    const highlight = hoverBaseSeries ? 'base' :
      hoverAdditionSeries ? 'addition' :
        hoverGuidelineSeries ? 'guideline' :
          'none';

    const serieLabels = typeSeries[type];
    const tooltipDescription = (typeof dateRange === string) ? dateRange.replace(/\s*\d{2}:\d{2}:\d{2}/g, '') : ' ';
    const fileName = `daypart_${type.toLowerCase()}`;
    const allCampaignData = getChartData(campaign);
    const campaignAvgAdCost = get(allCampaignData, 'avgAdCost', null);

    const { daypartAnalyticsChart, daypartAnalyticsTable } = showInReportConfig;
    let displayDaypartChart = false;
    let displayDaypartTable = false;
    let showDeleteDaypartChartButton = false;
    let showDeleteDaypartTableButton = false;

    if(isCustomReport) {
      displayDaypartChart = daypartAnalyticsChart && isDaypartTab;
      displayDaypartTable = daypartAnalyticsTable;
      showDeleteDaypartChartButton = daypartAnalyticsChart;
      showDeleteDaypartTableButton = daypartAnalyticsTable;
    }
    const isDisplayChart = (!isCustomReport && isDaypartTab || isCustomReport && displayDaypartChart) ;
    const isDisplayChartContainer = (!inpdf && (isDisplayChart || displayDaypartTable) || inpdf && isDisplayChart && isHiddenTable) ;
    const firstCalendar = get(campaign, 'firstCalendar.selected');
    const timezoneKey = get(campaign, 'timezoneSetting.timezoneKey');
    let cutOverDate;
     if (
      ga4CutOverDate &&
      isByDateOption &&
      withInRange({
        dateRange: firstCalendar,
        comparedDate: ga4CutOverDate,
        timezoneKey,
      })
    ) {
      cutOverDate = formatCutOverDateByTimeRange({
        cutOverDate: ga4CutOverDate,
        timeRange,
        dateFormatType: 'dayPart',
        campaignDateRange: firstCalendar,
        timezoneKey,
      });
    }
    const cutOverDateTooltip = `Web data source updated on ${formatCutOverDate({ cutOverDate: ga4CutOverDate, timezoneKey })}`;
    const isNewAdUserSelected = daypartDetailSelector.type === NEW_AD_USERS;

    return (
      <Fragment>
        {
          isDisplayChartContainer &&
            <div className={cx({[styles.itemContainer]: isCustomReport, [styles.deleteItemContainer]: currentStep === 'customize' && displayDaypartChart })}>
              {showDeleteDaypartChartButton &&
                <DeleteReportItemButton
                  isDelete={showDeleteDaypartChartButton}
                  parentId="daypart"
                  componentId="daypartAnalyticsChart"
                  customClassName={styles.deleteButton}
                  itemIndex={itemIndex}
                />
              }
              {isDisplayChart &&
                <ModuleExport
                  exportImg={this.exportContainer}
                  data={this.prepCsvData(chartData)}
                  fileName={fileName}
                  disableCsvExport
                />
              }
              <div data-test="daypartChart" className={cx(styles.detailsPresenter, className)} ref={this.exportContainer}>
                <div className={styles.daypartTitle}>
                  <div className={styles.text}>{title}</div>
                  {
                    showLocationDisclaimer &&
                    <UASunsetDisclaimer
                      title={GA4_DAYPART_MESSAGE}
                      customStyle={{
                        toastContainer: styles.toastContainer
                      }}
                    />
                  }
                </div>
                <div className={styles.controllers}>
                  <DataSelector
                    typeOptions={ isNewAdUsersEnabled ?
                                  dataTypes :
                                  [{
                                    value: 'adVisit',
                                    label: 'Ad Visits'
                                  }]
                                }
                    selectedOptions={daypartDetailSelector}
                    onSelectionChange={this.onSelectionChange}
                    isShowNewAdUsers={isShowNewAdUsers}
                  />
                  <SeriesSelector
                    baseColor={baseColor}
                    additionColor={additionColor}
                    guidelineColor={guidelineColor}
                    baseLabel={serieLabels.baseLabel}
                    additionLabel={serieLabels.additionLabel}
                    guidelineLabel={serieLabels.guidelineLabel}
                    showGuidelineSerie={showGuidelineSerie}
                    showBaseSerie={showBaseSerie}
                    showAdditionSerie={showAdditionSerie}
                    hoverGuidelineSeries={hoverGuidelineSeries}
                    hoverBaseSeries={hoverBaseSeries}
                    hoverAdditionSeries={hoverAdditionSeries}
                    onSelectionChange={this.onSelectionChange}
                    handleSerieSelectionHover={this.handleSerieSelectionHover}
                    showBaseOption={!isNewAdUserSelected}
                  />
                </div>
              {isDisplayChart ? (
                <div className={styles.charts}>
                  <DaypartCompilationChart
                    data={chartData}
                    baseColor={baseColor}
                    additionColor={additionColor}
                    guidelineColor={guidelineColor}
                    baseLabel={serieLabels.baseLabel}
                    guidelineLabel={serieLabels.guidelineLabel}
                    tooltipHeaderKey='time'
                    description={tooltipDescription}
                    totalValueTooltipLabel={serieLabels.totalLabel}
                    baseValueTooltipLabel={serieLabels.baseLabel}
                    additionalValueTooltipLabel={serieLabels.additionLabel}
                    guidelineValueTooltipLabel={serieLabels.guidelineLabel}
                    highlightValues={highlight}
                    showGuidelineValues={showGuidelineSerie}
                    showBaseValues={showBaseSerie && !isNewAdUserSelected}
                    showAdditionalValues={showAdditionSerie}
                    yAxisLabel={serieLabels.yAxisLabel}
                    cutOverDate={cutOverDate}
                    cutOverDateTooltip={cutOverDateTooltip}
                    showBaseOption={!isNewAdUserSelected}
                  />
                  {shouldShowNewAdUserSunsetDisclaimer(
                    campaign,
                    isNewAdUserSelected
                    ) && (
                    <NewAdUserSunsetDisclaimer
                        customStyle={styles.chartDisclaimer}
                    />
                  )} 
                </div>
                ) : ''
              }
            </div>
            </div>
        }
        {!isHiddenTable &&(
        <div>
          <DaypartTable
            data={tableData}
            campaignAvgAdCost={campaignAvgAdCost}
            onCellClick={this.handleCellClick}
            daypartDetailSelector={daypartDetailSelector}
            inpdf={inpdf}
            page={page}
            numberPage={numberPage}
            rowsPerPage={rowsPerPage}
            isGrandTotalDisplay={isGrandTotalDisplay}
            initialSortStatus={initialSortStatus}
            isCustomReport={isCustomReport}
            showDeleteDaypartTableButton={showDeleteDaypartTableButton}
            displayDaypartTable={displayDaypartTable}
            displayDaypartChart={daypartAnalyticsChart && displayChart}
            itemIndex={itemIndex}
            isShowNewAdUsers={isShowNewAdUsers}
            rowsFirstPage={rowsFirstPage}
          />
        </div>
      )}
      </Fragment>

    );
  }
}

import { connect } from 'react-redux';
import { getTableSortSelector } from '../../DashboardTab/Report/helper';
import { NEW_AD_USERS } from './constant';
import { shouldShowNewAdUserSunsetDisclaimer } from '../../../helpers/common';
import { advertiserShape } from '../../../helpers/propTypes';

function mapStateToProps(state, props) {
  return {
    currentStep: get(state, 'reportBuilder.currentStep', ''),
    tableSortSelector: getTableSortSelector(state, props, DAYPART_COMPILATION_CHART_ID),
  }
}

export default connect(mapStateToProps)(DaypartDetailsViewer);
